import Component from '@glimmer/component';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { VStack, Section } from 'tio-ui/components/layout';
import { Heading, Text } from 'tio-ui/components/typography';
import { formatCurrency } from 'tio-reimagined/format';
import Button from 'tio-reimagined/components/button';
import { t } from 'ember-intl';
import {
  PhSparkle,
  PhArrowRight,
  PhTarget,
  PhTrendUp,
  PhShieldCheck,
  PhBookOpen,
  PhGraduationCap,
  PhLock,
  PhBank,
  PhCompass,
} from 'ember-phosphor-icons';
import PageHero from 'tio-reimagined/components/page-hero';
import StatePill from 'tio-employee/components/reimagined/tas/state-pill';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type {
  DataSourceInfo,
  LiveTasApplication,
  LiveTasData,
  LiveTasProgram,
} from 'tio-employee/services/reimagined-data';

interface TasDashboardModel {
  tasData: LiveTasData;
  tasUsedAmount: { yearToDate: number; isLiveData: boolean };
  dataSourceInfo: DataSourceInfo;
}

interface TasDashboardSignature {
  Args: {
    model: TasDashboardModel;
  };
}

export default class CareerPathwaysTasDashboard extends Component<TasDashboardSignature> {
  @service declare reimaginedData: ReimaginedDataService;

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get isEligible() {
    return this.args.model.tasData.isEligible;
  }

  get tasSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.args.model.tasData.isLiveData ? 'live' : 'placeholder';
  }

  get programs(): LiveTasProgram[] {
    return this.args.model.tasData.programs;
  }

  get applications(): LiveTasApplication[] {
    return this.args.model.tasData.applications;
  }

  get totalApproved() {
    return this.args.model.tasData.summary.totalApproved;
  }

  get totalPaid() {
    return this.args.model.tasData.summary.totalPaid;
  }

  get activeCount() {
    return this.args.model.tasData.summary.activeCount;
  }

  get pendingCount() {
    return this.args.model.tasData.summary.pendingCount;
  }

  <template>
    {{#if this.isEligible}}
      <VStack class="gap-10">
        <PageHero
          @gradientClass="bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600"
          @rounded="3xl"
          class="shadow-xl"
        >
          <:default>
            <div class="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div class="max-w-2xl">
                <div class="flex items-center gap-2 text-sm font-semibold">
                  <PhSparkle class="h-4 w-4" />
                  <span>{{t "tuition_assistance.default"}}</span>
                </div>
                <Heading @level={{1}} class="text-white">
                  {{t "tuition_assistance.reimagined.dashboard.hero_title"}}
                </Heading>
                <Text class="text-white/80">
                  {{t "tuition_assistance.reimagined.dashboard.hero_subtitle"}}
                </Text>
                <div class="mt-6 flex flex-wrap gap-3">
                  <LinkTo @route="authenticated.tas.dashboard">
                    <Button @intent="primary">
                      {{t "tuition_assistance.reimagined.dashboard.cta_start_request"}}
                    </Button>
                  </LinkTo>
                  <LinkTo @route="authenticated.reimagined.tas.programs">
                    <Button
                      @intent="primary"
                      @appearance="outlined"
                      class="border-white/50 text-white bg-white/15 hover:bg-white/25"
                    >
                      {{t "tuition_assistance.reimagined.dashboard.cta_explore_programs"}}
                    </Button>
                  </LinkTo>
                </div>
              </div>
              <div class="grid w-full max-w-md grid-cols-2 gap-4">
                <div class="rounded-2xl bg-white/10 p-4">
                  <div
                    class="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-white/70"
                  >
                    <PhTarget class="h-4 w-4" />
                    {{t "tuition_assistance.reimagined.dashboard.metric_approved"}}
                  </div>
                  <Heading @level={{3}} class="text-white">
                    {{formatCurrency this.totalApproved}}
                  </Heading>
                </div>
                <div class="rounded-2xl bg-white/10 p-4">
                  <div
                    class="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-white/70"
                  >
                    <PhTrendUp class="h-4 w-4" />
                    {{t "tuition_assistance.reimagined.dashboard.metric_paid"}}
                  </div>
                  <Heading @level={{3}} class="text-white">
                    {{formatCurrency this.totalPaid}}
                  </Heading>
                </div>
                <div class="rounded-2xl bg-white/10 p-4">
                  <div
                    class="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-white/70"
                  >
                    <PhShieldCheck class="h-4 w-4" />
                    {{t "tuition_assistance.reimagined.dashboard.metric_active"}}
                  </div>
                  <Heading @level={{3}} class="text-white">
                    {{this.activeCount}}
                  </Heading>
                </div>
                <div class="rounded-2xl bg-white/10 p-4">
                  <div
                    class="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-white/70"
                  >
                    <PhBookOpen class="h-4 w-4" />
                    {{t "tuition_assistance.reimagined.dashboard.metric_pending"}}
                  </div>
                  <Heading @level={{3}} class="text-white">
                    {{this.pendingCount}}
                  </Heading>
                </div>
              </div>
            </div>
          </:default>
        </PageHero>

        <DataIndicator @source={{this.tasSource}} @showBadge={{true}} @dataSourceId="tas-dashboard">
          <Section>
            <:header>
              <div class="flex items-center justify-between">
                <Heading @level={{2}}>{{t
                    "tuition_assistance.reimagined.dashboard.section_momentum"
                  }}</Heading>
                <LinkTo
                  @route="authenticated.reimagined.tas.applications"
                  class="tio-copy text-sm font-semibold"
                >
                  {{t "tuition_assistance.reimagined.dashboard.link_view_activity"}}
                </LinkTo>
              </div>
            </:header>
            <:body>
              <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div class="rounded-2xl ring-1 ring-gray-950/[0.04] bg-white p-5 shadow-sm">
                  <div class="flex items-center justify-between">
                    <Text @intent="muted">{{t
                        "tuition_assistance.reimagined.dashboard.card_used_this_year"
                      }}</Text>
                    <PhGraduationCap class="h-5 w-5 text-indigo-500" />
                  </div>
                  <Heading @level={{3}}>
                    {{formatCurrency @model.tasUsedAmount.yearToDate}}
                  </Heading>
                  <Text @intent="muted" class="text-sm">
                    {{t "tuition_assistance.reimagined.dashboard.card_used_this_year_hint"}}
                  </Text>
                </div>
                <div class="rounded-2xl ring-1 ring-gray-950/[0.04] bg-white p-5 shadow-sm">
                  <div class="flex items-center justify-between">
                    <Text @intent="muted">{{t
                        "tuition_assistance.reimagined.dashboard.card_approval_pipeline"
                      }}</Text>
                    <PhShieldCheck class="h-5 w-5 text-emerald-500" />
                  </div>
                  <Heading @level={{3}}>
                    {{t
                      "tuition_assistance.reimagined.dashboard.pending_count"
                      count=this.pendingCount
                    }}
                  </Heading>
                  <Text @intent="muted" class="text-sm">
                    {{t "tuition_assistance.reimagined.dashboard.card_approval_pipeline_hint"}}
                  </Text>
                </div>
                <div class="rounded-2xl ring-1 ring-gray-950/[0.04] bg-white p-5 shadow-sm">
                  <div class="flex items-center justify-between">
                    <Text @intent="muted">{{t
                        "tuition_assistance.reimagined.dashboard.card_funding_delivered"
                      }}</Text>
                    <PhTrendUp class="h-5 w-5 text-fuchsia-500" />
                  </div>
                  <Heading @level={{3}}>
                    {{formatCurrency this.totalPaid}}
                  </Heading>
                  <Text @intent="muted" class="text-sm">
                    {{t "tuition_assistance.reimagined.dashboard.card_funding_delivered_hint"}}
                  </Text>
                </div>
              </div>
            </:body>
          </Section>
        </DataIndicator>

        <Section>
          <:header>
            <div class="flex items-center justify-between">
              <Heading @level={{2}}>{{t
                  "tuition_assistance.reimagined.dashboard.section_active_programs"
                }}</Heading>
              <LinkTo
                @route="authenticated.reimagined.tas.programs"
                class="tio-copy text-sm font-semibold"
              >
                {{t "tuition_assistance.reimagined.dashboard.link_view_programs"}}
              </LinkTo>
            </div>
          </:header>
          <:body>
            {{#if this.programs.length}}
              <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {{#each this.programs as |program|}}
                  <div class="rounded-2xl ring-1 ring-gray-950/[0.04] bg-white p-5 shadow-sm">
                    <div class="flex items-start justify-between gap-4">
                      <div>
                        <Heading @level={{3}}>{{program.name}}</Heading>
                        <Text @intent="muted">{{program.institution}}</Text>
                        {{#if program.startDate}}
                          <Text @intent="muted">
                            {{t
                              "tuition_assistance.reimagined.dashboard.label_starts"
                              date=program.startDate
                            }}
                          </Text>
                        {{/if}}
                      </div>
                      <StatePill @state={{program.state}} />
                    </div>
                    <div class="mt-4 flex items-center justify-between">
                      <Text @intent="muted">
                        {{t
                          "tuition_assistance.reimagined.dashboard.label_major"
                          major=program.major
                        }}
                      </Text>
                      <LinkTo
                        @route="authenticated.tas.programs.show"
                        @model={{program.id}}
                        class="tio-copy text-sm font-semibold"
                      >
                        {{t "tuition_assistance.reimagined.dashboard.link_open_program"}}
                        <PhArrowRight class="ml-1 inline h-4 w-4" />
                      </LinkTo>
                    </div>
                  </div>
                {{/each}}
              </div>
            {{else}}
              <div class="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-8">
                <Heading @level={{3}}>{{t
                    "tuition_assistance.reimagined.dashboard.empty_programs_title"
                  }}</Heading>
                <Text @intent="muted">
                  {{t "tuition_assistance.reimagined.dashboard.empty_programs_body"}}
                </Text>
                <div class="mt-4">
                  <LinkTo @route="authenticated.tas.dashboard">
                    <Button @intent="primary">{{t
                        "tuition_assistance.reimagined.dashboard.empty_programs_cta"
                      }}</Button>
                  </LinkTo>
                </div>
              </div>
            {{/if}}
          </:body>
        </Section>

        <Section>
          <:header>
            <div class="flex items-center justify-between">
              <Heading @level={{2}}>{{t
                  "tuition_assistance.reimagined.dashboard.section_recent_applications"
                }}</Heading>
              <LinkTo
                @route="authenticated.reimagined.tas.applications"
                class="tio-copy text-sm font-semibold"
              >
                {{t "tuition_assistance.reimagined.dashboard.link_view_applications"}}
              </LinkTo>
            </div>
          </:header>
          <:body>
            {{#if this.applications.length}}
              <div class="grid grid-cols-1 gap-4">
                {{#each this.applications as |application|}}
                  <div class="rounded-2xl ring-1 ring-gray-950/[0.04] bg-white p-5 shadow-sm">
                    <div class="flex items-start justify-between gap-4">
                      <div>
                        <Heading @level={{3}}>{{application.name}}</Heading>
                        <Text @intent="muted">{{application.programName}}</Text>
                        {{#if application.coursesBeginDate}}
                          <Text @intent="muted">
                            {{t
                              "tuition_assistance.reimagined.dashboard.label_starts"
                              date=application.coursesBeginDate
                            }}
                          </Text>
                        {{/if}}
                      </div>
                      <div class="flex flex-wrap items-center gap-2">
                        <StatePill @state={{application.state}} />
                        <StatePill @state={{application.paymentState}} />
                      </div>
                    </div>
                    <div class="mt-4 flex items-center justify-between">
                      <Text @intent="muted">
                        {{t
                          "tuition_assistance.reimagined.dashboard.label_approved_amount"
                          amount=(formatCurrency application.approvedTotal)
                        }}
                      </Text>
                      <LinkTo
                        @route="authenticated.tas.applications.show"
                        @model={{application.id}}
                        class="tio-copy text-sm font-semibold"
                      >
                        {{t "tuition_assistance.reimagined.dashboard.link_review_application"}}
                        <PhArrowRight class="ml-1 inline h-4 w-4" />
                      </LinkTo>
                    </div>
                  </div>
                {{/each}}
              </div>
            {{else}}
              <div class="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-8">
                <Heading @level={{3}}>{{t
                    "tuition_assistance.reimagined.dashboard.empty_applications_title"
                  }}</Heading>
                <Text @intent="muted">
                  {{t "tuition_assistance.reimagined.dashboard.empty_applications_body"}}
                </Text>
                <div class="mt-4">
                  <LinkTo @route="authenticated.tas.dashboard">
                    <Button @intent="primary">{{t
                        "tuition_assistance.reimagined.dashboard.empty_applications_cta"
                      }}</Button>
                  </LinkTo>
                </div>
              </div>
            {{/if}}
          </:body>
        </Section>
      </VStack>
    {{else}}
      {{! template-lint-disable no-bare-strings }}
      <VStack class="gap-10">
        <PageHero
          @gradientClass="bg-gradient-to-br from-gray-400 via-gray-500 to-gray-600"
          @rounded="3xl"
          class="shadow-xl"
        >
          <:default>
            <div class="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div class="max-w-2xl">
                <div class="flex items-center gap-2 text-sm font-semibold">
                  <PhLock class="h-4 w-4" />
                  <span>{{t "tuition_assistance.default"}}</span>
                </div>
                <Heading @level={{1}} class="text-white">
                  Tuition Assistance Is Not Available
                </Heading>
                <Text class="text-white/80">
                  Your employer does not currently offer a Tuition Assistance benefit. Explore your
                  other benefits below.
                </Text>
              </div>
              <div class="flex h-32 w-32 items-center justify-center rounded-full bg-white/10">
                <PhGraduationCap class="h-16 w-16 text-white/40" />
              </div>
            </div>
          </:default>
        </PageHero>

        <Section>
          <:header>
            <Heading @level={{2}}>Explore Your Other Benefits</Heading>
          </:header>
          <:body>
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
              <LinkTo @route="authenticated.reimagined.loans" class="block">
                <div
                  class="rounded-2xl ring-1 ring-gray-950/4 bg-white p-6 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div class="flex items-center gap-4">
                    <div
                      class="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-100"
                    >
                      <PhBank class="h-6 w-6 text-emerald-600" />
                    </div>
                    <div>
                      <Heading @level={{3}}>Student Loan Repayment</Heading>
                      <Text @intent="muted">Your employer contributes toward your student loans</Text>
                    </div>
                    <PhArrowRight class="ml-auto h-5 w-5 text-gray-400" />
                  </div>
                </div>
              </LinkTo>
              <LinkTo @route="authenticated.reimagined.benefits" class="block">
                <div
                  class="rounded-2xl ring-1 ring-gray-950/4 bg-white p-6 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div class="flex items-center gap-4">
                    <div
                      class="flex h-12 w-12 items-center justify-center rounded-xl bg-violet-100"
                    >
                      <PhCompass class="h-6 w-6 text-violet-600" />
                    </div>
                    <div>
                      <Heading @level={{3}}>All Benefits</Heading>
                      <Text @intent="muted">View all available benefits and programs</Text>
                    </div>
                    <PhArrowRight class="ml-auto h-5 w-5 text-gray-400" />
                  </div>
                </div>
              </LinkTo>
            </div>
          </:body>
        </Section>

        <div class="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
          <PhGraduationCap class="mx-auto h-12 w-12 text-gray-300" />
          <Heading @level={{3}} class="mt-4">Interested in Tuition Assistance?</Heading>
          <Text @intent="muted" class="mt-2 max-w-lg mx-auto">
            Talk to your HR department about adding a Tuition Assistance benefit to your company's
            education package.
          </Text>
        </div>
      </VStack>
    {{/if}}
  </template>
}
